import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { GenericConstants } from '../enums/generic-constants.enum';

type FeatureFlags = Record<string, boolean>;

/**
 * Feature flags resueltas por el backend para el usuario autenticado.
 *
 * El front solo pregunta "¿qué tengo habilitado?" (GET feature-flags/me); la decisión de rollout
 * (off / whitelist / everyone) vive en el backend. Se cachea en un BehaviorSubject como el resto de
 * estado de la app (ver ActiveSiteService / AppSettingsService).
 */
@Injectable({ providedIn: 'root' })
export class FeatureFlagService {
  private readonly BACKEND_URL = environment.baseUrl + 'feature-flags/me';

  private flagsSubject = new BehaviorSubject<FeatureFlags>({});
  public flags$ = this.flagsSubject.asObservable();

  constructor(private http: HttpClient) {}

  loadFlags(): Observable<FeatureFlags> {
    // El backoffice no tiene interceptor que inyecte el token: se añade a mano, como en POSService.
    const token = localStorage.getItem(GenericConstants.STORAGE_ACCESS_TOKEN) as string;
    const headers = new HttpHeaders().set('Authorization', token);
    return this.http.get<FeatureFlags>(this.BACKEND_URL, { headers }).pipe(
      tap(flags => this.flagsSubject.next(flags ?? {}))
    );
  }

  /**
   * Para APP_INITIALIZER: solo pide flags si hay sesión, y nunca bloquea el arranque ante un fallo
   * (un error de red no debe impedir cargar el backoffice).
   */
  initializeFlags(): Promise<FeatureFlags> {
    const token = localStorage.getItem(GenericConstants.STORAGE_ACCESS_TOKEN);
    if (!token) {
      return Promise.resolve({});
    }
    return this.loadFlags()
      .pipe(catchError(() => of<FeatureFlags>({})))
      .toPromise()
      .then(flags => flags ?? {});
  }

  isEnabled(key: string): boolean {
    return this.flagsSubject.value[key] === true;
  }

  get flags(): FeatureFlags {
    return this.flagsSubject.value;
  }
}
